import { useEffect, useRef, useState } from 'react'
import type { Filter } from '@renderer/types/configState'
import { imageUtils } from '@renderer/utils/imageUtils'

/** How often the wall's faces refresh. */
const REFRESH_MS = 2000

/**
 * The guest's own face through every filter, for the gallery wall.
 *
 * Fed from `latestFrame` — the raw frames the big preview already pulls from
 * the camera (see Filter.tsx), so the wall never asks the camera for anything.
 * Every two seconds the newest frame is cut down to the label's shape at
 * twice its size and baked through each filter in turn with the same
 * `applyFilterToImage` the print uses. One pass at a time: if a slow booth
 * takes longer than the interval, the next tick is skipped rather than queued.
 *
 * Returns filter id → object URL; URLs from the previous pass are revoked.
 */
export function useFilterThumbs(
  filters: Filter[],
  latestFrame: React.MutableRefObject<Blob | null>,
  size: { width: number; height: number }
): Record<string, string> {
  const [thumbs, setThumbs] = useState<Record<string, string>>({})
  const busyRef = useRef(false)
  const liveRef = useRef<Record<string, string>>({})
  const filtersRef = useRef(filters)
  filtersRef.current = filters

  useEffect(() => {
    let cancelled = false

    const refresh = async (): Promise<void> => {
      const frame = latestFrame.current
      if (busyRef.current || !frame || filtersRef.current.length === 0) return
      busyRef.current = true
      const created: string[] = []
      try {
        const source = await cropTo(frame, size.width * 2, size.height * 2)
        created.push(source)
        const next: Record<string, string> = {}
        for (const filter of filtersRef.current) {
          if (cancelled) return
          const url = await imageUtils.applyFilterToImage(source, filter)
          next[filter._id] = url
          if (url !== source) created.push(url)
        }
        if (cancelled) return
        const previous = liveRef.current
        liveRef.current = next
        setThumbs(next)
        for (const url of Object.values(previous)) URL.revokeObjectURL(url)
        // The un-filtered crop is only an input; each thumb holds its own copy.
        if (!Object.values(next).includes(source)) URL.revokeObjectURL(source)
      } catch (error) {
        console.warn('[BK] filter wall refresh failed:', error)
        for (const url of created) URL.revokeObjectURL(url)
      } finally {
        busyRef.current = false
      }
    }

    void refresh()
    const timer = setInterval(() => void refresh(), REFRESH_MS)
    return (): void => {
      cancelled = true
      clearInterval(timer)
      for (const url of Object.values(liveRef.current)) URL.revokeObjectURL(url)
      liveRef.current = {}
    }
  }, [latestFrame, size.width, size.height])

  return thumbs
}

/** Centre-crop a frame to `width` x `height`, as an object URL. */
export async function cropTo(frame: Blob, width: number, height: number): Promise<string> {
  const bitmap = await createImageBitmap(frame)
  const scale = Math.max(width / bitmap.width, height / bitmap.height)
  const w = bitmap.width * scale
  const h = bitmap.height * scale
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('no 2d context')
  ctx.drawImage(bitmap, (width - w) / 2, (height - h) / 2, w, h)
  bitmap.close()
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85)
  )
  if (!blob) throw new Error('crop failed')
  return URL.createObjectURL(blob)
}
